import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Popup from "../Popup/Popup";
import InputComponent from "../../components/DynamicInput/DynamicInput";
import { FormFields } from "./formDummyData";
import useEventsQueries from "../../../store/events/eventsQueries.js";
import {
  EVENT_CODES,
  EVENT_CODES_NAMES,
} from "../../../constants/eventConstants.js";
import closeIcon from "../../../assets/images/close_icon.svg";

import "./CreateEventFormPopup.css";
import { CONFIG_VARS } from "../../../constants/urlConstants/configuration.js";

const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const CreateEventFormPopup = ({ setIsPopupVisible, isPopupVisible }) => {
  const {
    handleSearchSchools,
    handleGetAllGrades,
    handleGetEventLevels,
    handleGetUpcomingRounds,
    handleCreateEvent,
    handleGetUserAgents,
  } = useEventsQueries();

  const toggle1 = {
    type: "toggle",
  };

  const initialFormData = {
    schoolname: "",
    eventtype: "",
    eventname: "",
    eventlevel: "",
    round: "",
    grade: "",
    startdate: "",
    enddate: "",
    starttime: "",
    endtime: "",
    isPaidReport: true,
    description: "",
    sr_manager: "",
    manager: "",
    rep: "",
  };

  const [schoolOptions, setSchoolOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [eventLevelOptions, setEventLevelOptions] = useState([]);
  const [roundOptions, setRoundOptions] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [dynamicErrorMessage, setDynamicErrorMessage] = useState("");
  const [formData, setFormData] = useState(initialFormData);
  const [userAgents, setUserAgents] = useState([]);

  const isFormValid = () => {
    const requiredFields = [
      "eventtype",
      "eventname",
      "eventlevel",
      "round",
      "grade",
      "startdate",
      "enddate",
      "starttime",
      "endtime",
      "sr_manager",
      "manager",
      "rep",
      "eventMode",
    ];

    const isSemiFinalsOrFinals =
      formData.eventlevel === "Semi-Finals" || formData.eventlevel === "Finals";

    if (!isSemiFinalsOrFinals) {
      requiredFields.push("schoolname");
    }

    return requiredFields.every((field) => formData[field] !== "");
  };

  const togglePopup = () => {
    if (isPopupVisible) {
      setFormData(initialFormData);
    } else {
      setShowNotification(false);
      setShowErrorNotification(false);
    }
    setIsPopupVisible(!isPopupVisible);
  };

  useEffect(() => {
    const fetchUserAgents = async () => {
      const result = await handleGetUserAgents();
      if (result && result.agents) {
        const formattedAgents = result.agents.map((agent) => ({
          ...agent, // Keep all original properties
          label: `${agent.first_name} ${agent.last_name} - (${agent.admission_number})`,
          value: agent.crn_id,
        }));
        setUserAgents(formattedAgents);
      }
    };
    fetchUserAgents();
  }, []);

  useEffect(() => {
    const fetchGrades = async () => {
      if (!formData.eventname) {
        // Reset grade options if no event name is selected
        setGradeOptions([]);
        setAllGrades([]);
        return;
      }

      const response = await handleGetAllGrades();
      if (response) {
        let formattedGrades = response.map((grade) => ({
          label: grade.grade_name,
          value: grade.grade_id.toString(),
        }));

        // Filter grades for Career Counselling to only show Grade-8 to Grade-10 (IDs 8-10)
        if (formData.eventname === "Career Counselling") {
          formattedGrades = formattedGrades.filter((grade) => {
            const gradeId = parseInt(grade.value);
            return gradeId >= 8 && gradeId <= 10;
          });
        }

        // Filter grades for Youth Entrepreneurship Challenge to only show Grade-9 to Grade-12 (IDs 9-12)
        if (formData.eventname === "Youth Entrepreneurship Challenge") {
          formattedGrades = formattedGrades.filter((grade) => {
            const gradeId = parseInt(grade.value);
            return gradeId >= 9 && gradeId <= 12;
          });
        }

        const gradesWithAll = [
          { label: "All", value: "all" },
          ...formattedGrades,
        ];
        setAllGrades(formattedGrades);
        setGradeOptions(gradesWithAll);
      }
    };
    fetchGrades();
  }, [formData.eventname]);

  useEffect(() => {
    const fetchSchools = async () => {
      if (formData.schoolname.length >= 3) {
        const response = await handleSearchSchools(formData.schoolname);
        if (response) {
          const formattedSchools = response.data.map((school) => ({
            label: `${school._source.school_name} - (${school._source.school_id})`,
            value: `${school._source.school_name} - (${school._source.school_id})`,
          }));
          setSchoolOptions(formattedSchools);
        }
      }
    };

    const debounceTimeout = setTimeout(fetchSchools, 400);
    return () => clearTimeout(debounceTimeout);
  }, [formData.schoolname]);

  useEffect(() => {
    const fetchEventLevels = async () => {
      if (formData.eventname) {
        const eventName =
          EVENT_CODES_NAMES[formData.eventname] || formData.eventname;
        const levelsResponse = await handleGetEventLevels(eventName);
        if (levelsResponse.error) {
          console.error("Error in fetching levels", levelsResponse.error);
        } else if (levelsResponse.levels) {
          if (levelsResponse.levels.length > 0) {
            setEventLevelOptions(
              levelsResponse.levels.map((level) => ({
                label: level,
                value: level,
              }))
            );
          } else {
            setEventLevelOptions([
              {
                label: "Prelims",
                value: "Prelims",
              },
            ]);
          }
        }
      }
    };
    fetchEventLevels();
  }, [formData.eventname]);

  useEffect(() => {
    const fetchRounds = async () => {
      const schoolIdValue = formData.schoolname.match(/\((.*?)\)/)
        ? formData.schoolname.match(/\((.*?)\)/)[1]
        : "";

      const isSemiFinalsOrFinals =
        formData.eventlevel === "Semi-Finals" ||
        formData.eventlevel === "Finals";

      if (formData.eventlevel && (isSemiFinalsOrFinals || schoolIdValue)) {
        const body = {
          event_code: formData.eventname,
          level: formData.eventlevel,
        };

        if (!isSemiFinalsOrFinals) {
          body.school_id = schoolIdValue;
        }

        const roundsResponse = await handleGetUpcomingRounds(body);

        if (roundsResponse && roundsResponse.rounds) {
          const newRoundOptions = roundsResponse.rounds.map((round) => ({
            label: round,
            value: round,
          }));
          setRoundOptions(newRoundOptions);
          if (newRoundOptions.length > 0) {
            setFormData((prevData) => ({
              ...prevData,
              round: newRoundOptions[0].value,
            }));
          } else {
            setRoundOptions([{ label: "No rounds found", value: "" }]);
            setFormData((prevData) => ({
              ...prevData,
              round: "",
            }));
          }
        } else {
          setRoundOptions([
            { label: "Fill All Required Fields before this", value: "" },
          ]);
          setFormData((prevData) => ({
            ...prevData,
            round: "",
          }));
        }
      } else {
        setRoundOptions([
          { label: "Fill All Required Fields before this", value: "" },
        ]);
        setFormData((prevData) => ({
          ...prevData,
          round: "",
        }));
      }
    };

    fetchRounds();
  }, [formData.eventlevel, formData.eventname, formData.schoolname]);

  const handleInputChange = (field) => (event) => {
    const value =
      field === "isPaidReport" ? event.target.checked : event.target.value;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const isSchoolDisabled =
    formData.eventname &&
    (formData.eventlevel === "Semi-Finals" || formData.eventlevel === "Finals");

  useEffect(() => {
    if (isSchoolDisabled && formData.schoolname) {
      setFormData((prevData) => ({
        ...prevData,
        schoolname: "",
      }));
    }
  }, [isSchoolDisabled, formData.schoolname]);

  const onSaveAndPublish = async () => {
    const schoolIdValue = formData.schoolname.match(/\((.*?)\)/)
      ? formData.schoolname.match(/\((.*?)\)/)[1]
      : null;

    const eventName =
      EVENT_CODES_NAMES[formData.eventname] || formData.eventname;
    const createEventRequestBody = {
      event_code: formData.eventname,
      name: eventName,
      description: formData.description,
      provider_metadata: {
        event_link:
          "https://rpw4ykq3qjfjetk5zfiulfqvsa0lkknr.lambda-url.ap-south-1.on.aws/",
        event_provider: "OneASM",
      },
      target_groups:
        formData.grade === "all"
          ? allGrades.map((grade) => ({
              school_id: schoolIdValue,
              grade: grade.value,
            }))
          : [
              {
                school_id: schoolIdValue,
                grade: formData.grade,
              },
            ],
      specification: {
        event_type: formData.eventtype,
        level: formData.eventlevel,
        round: formData.round,
        credit_requirement: {
          consumption_type: "FREE",
          amount: 0,
          is_paid_report: formData.isPaidReport,
          total_students: 0,
        },
      },
      start_date_time: `${formData.startdate}T${formData.starttime}`,
      end_date_time: `${formData.enddate}T${formData.endtime}`,
      is_paid_report: formData.isPaidReport,
      is_editable_by_school_admin: false,
      sr_manager: formData.sr_manager,
      manager: formData.manager,
      rep: formData.rep,
    };

    const result = await handleCreateEvent(createEventRequestBody);
    if (result && result.message === "Event creation in progress") {
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
      togglePopup();
    } else if (result?.error?.status === 409) {
      const errorMessage =
        result.error.data?.message ||
        "A scheduling conflict occurred. Please check the details and try again.";
      setDynamicErrorMessage(errorMessage);
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
        setDynamicErrorMessage("");
      }, 7000);
    } else if (result?.error?.status === 403) {
      const errorMessage =
        result.error.data?.message || "Login Expired. Please login again.";
      setDynamicErrorMessage(errorMessage);
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
        setDynamicErrorMessage("");
      }, 5000);
    } else if (result?.error) {
      const errorMessage =
        result.error.data?.message ||
        result.error.message ||
        "An unexpected error occurred while creating the event.";
      setDynamicErrorMessage(errorMessage);
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
        setDynamicErrorMessage("");
      }, 5000);
    } else {
      console.error(
        "Error creating event: Unknown error or unexpected response structure",
        result
      );
      setDynamicErrorMessage("An unknown error occurred. Please try again.");
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
        setDynamicErrorMessage("");
      }, 5000);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
    setDynamicErrorMessage("");
  };

  const hierarchyRoleIds = CONFIG_VARS.hierarchy_role_ids[1]; // Get the prod environment roles
  const SENIOR_MANAGER_ROLE_ID = hierarchyRoleIds["Senior Manager"];
  const MANAGER_ROLE_ID = hierarchyRoleIds["Manager"];
  const EXECUTION_REP_ROLE_ID = hierarchyRoleIds["Execution Rep"];
  const seniorManagers = userAgents
    .filter((agent) => agent.roles?.includes(SENIOR_MANAGER_ROLE_ID))
    .sort((a, b) => a.label.localeCompare(b.label));
  const managers = userAgents
    .filter((agent) => agent.roles?.includes(MANAGER_ROLE_ID))
    .sort((a, b) => a.label.localeCompare(b.label));
  const executionReps = userAgents
    .filter((agent) => agent.roles?.includes(EXECUTION_REP_ROLE_ID))
    .sort((a, b) => a.label.localeCompare(b.label));
  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        title={"Create Event"}
        onClose={togglePopup}
        FirstBtnTitle={"Cancel"}
        SecondBtnTitle={"Save & Publish"}
        handelSecondry={togglePopup}
        handelPrimery={onSaveAndPublish}
        primaryButtonDisabled={!isFormValid()}
        contentClassName="create-event-popup__content"
      >
        <div
          className="createEvetnFormCont"
          role="group"
          aria-label="Create event form fields"
        >
          {FormFields.map((item, index) => {
            const fieldName = item.label.replace(/\s+/g, "").toLowerCase();
            let currentConfig = { ...item };
            if (item.label === "Round") {
              currentConfig.disabled = true;
              currentConfig.tooltip = "Round is selected automatically";
            }
            if (item.label === "School Name") {
              currentConfig.disabled = isSchoolDisabled;
              if (isSchoolDisabled) {
                currentConfig.placeholder =
                  "Disabled for Semi-Finals and Finals";
              }
            }
            return (
              <InputComponent
                key={index}
                config={currentConfig}
                value={formData[fieldName] || ""}
                options={{
                  schoolOptions: schoolOptions,
                  eventNames: EVENT_CODES,
                  eventLevelOptions: eventLevelOptions,
                  roundOptions: roundOptions,
                  gradeOptions: gradeOptions,
                }}
                onChange={handleInputChange(fieldName)}
              />
            );
          })}
          <div className="createEvetntoggleCont" style={{ marginTop: "20px" }}>
            <div className="toggleCont">
              <h4>
                Assign Team
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="assign-team-fields" style={{ marginTop: "20px" }}>
                <InputComponent
                  config={{
                    label: "Senior Manager",
                    type: "select",
                    options: seniorManagers,
                    placeholder: "Select Senior Manager",
                  }}
                  value={formData.sr_manager || ""}
                  onChange={handleInputChange("sr_manager")}
                />

                <InputComponent
                  config={{
                    label: "Manager",
                    type: "select",
                    options: managers,
                    placeholder: "Select L2 Manager",
                  }}
                  value={formData.manager || ""}
                  onChange={handleInputChange("manager")}
                />

                <InputComponent
                  config={{
                    label: "Execution Rep",
                    type: "select",
                    options: executionReps,
                    placeholder: "Select Rep",
                  }}
                  value={formData.rep || ""}
                  onChange={handleInputChange("rep")}
                />
              </div>
            </div>
          </div>
          <div className="createEvetntoggleCont" style={{ marginTop: "20px" }}>
            <div className="toggleCont">
              <h4>
                Administrative control
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div
                className="admin-control-fields"
                style={{ marginTop: "20px" }}
              >
                <p className="input-text-label">Is Paid Report?</p>
                <InputComponent
                  config={toggle1}
                  value={formData.isPaidReport}
                  onChange={handleInputChange("isPaidReport")}
                />
              </div>
            </div>
          </div>
          <div className="createEvetntoggleCont" style={{ marginTop: "20px" }}>
            <div className="toggleCont">
              <h4>
                Event Mode
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="event-mode-fields" style={{ marginTop: "20px" }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="radio"
                    id="offline"
                    name="eventtype"
                    value="OFFLINE"
                    checked={formData.eventtype === "OFFLINE"}
                    onChange={handleInputChange("eventtype")}
                    required
                  />
                  <label htmlFor="offline">Offline</label>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="radio"
                    id="online"
                    name="eventtype"
                    value="ONLINE"
                    checked={formData.eventtype === "ONLINE"}
                    onChange={handleInputChange("eventtype")}
                    required
                  />
                  <label htmlFor="online">Online</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      {showNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #008000",
              zIndex: 1000,
            }}
          >
            <button
              onClick={handleCloseNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#008000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Event Creation In Progress
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
              }}
            >
              Please check back after sometime
            </p>
          </div>
        </NotificationPortal>
      )}
      {showErrorNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #ff0000",
              zIndex: 1000,
              maxWidth: "400px",
            }}
          >
            <button
              onClick={handleCloseErrorNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#ff0000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Event Creation Failed
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
                wordBreak: "break-word",
              }}
            >
              {dynamicErrorMessage}
            </p>
          </div>
        </NotificationPortal>
      )}
    </>
  );
};

export default CreateEventFormPopup;
